import type { EquippedSlot, SlotDto } from '../api/wardrobe';
import { itemIconPath } from '../compositor/assetPaths';
import { slotLabel } from '../lib/slotLabel';
import { SLOT_GROUPS, type SlotGroupCode } from './slotGroups';
import { GroupGlyph } from './groupIcons';

// Mirrors WardrobeService.STAGE_ORDER exactly — hatchling < juvenile < adult — applied here to
// decide which groups the strip renders as locked.
const STAGE_ORDER = ['hatchling', 'juvenile', 'adult'];
function stageRank(stage: string): number {
  const i = STAGE_ORDER.indexOf(stage);
  return i < 0 ? 0 : i;
}

// `slotLabel` moved to `lib/slotLabel` when the bag's item-detail sheet needed to name a slot as
// well — see that module's header for why a transform rather than a table. Re-exported here so
// this module's existing importers (WardrobeScreen) are unchanged.
export { slotLabel };

export type SlotRailProps = {
  slotDefs: SlotDto[];
  equipped: Record<string, EquippedSlot>;
  stage: string;
  activeGroup: SlotGroupCode | null;
  onSelectGroup: (group: SlotGroupCode) => void;
  /** itemCode -> display name, for a worn group's accessible name (review finding I7, widened:
   *  "Head — Acorn Cap, Sun Specs", not just "Head" — a screen-reader user can't otherwise tell
   *  WHAT is worn without also opening the drawer). Optional so the gallery/tests can render a
   *  worn state without wiring a full owned-items catalogue when it isn't the point. */
  itemNamesByCode?: Record<string, string>;
  /** `'strip'` (default) is the drawer's five-column row. `'list'` (the Studio's left panel at
   *  studio3, Task 10) stacks the same five controls with the label beside the glyph and the worn
   *  pieces' own icons at the row's end — a 240-wide column has the room a 412-wide row does not.
   *  Same roles, same accessible names, same `.slot-rail-item`/`.slot-active` classes: only the
   *  root's extra `slot-rail-list` class and the thumbnails differ. */
  orientation?: 'strip' | 'list';
};

/**
 * SCREENS.md "/wardrobe — category strip" (2026-09-14, evening). Five tabs in one row at the top
 * of the item drawer, one per `SLOT_GROUPS` entry: a glyph, the group's FULL label, and a
 * worn-count badge. The accessible name lists the worn pieces (I7's rule) so a screen-reader user
 * still learns WHAT is worn without opening the drawer.
 *
 * A ROW AGAIN, AND WHY. This was a horizontal rail in Phase 1, stood on its end as a vertical dock
 * over the preview's right margin on 2026-08-20 because a row showed five of TWENTY-TWO slot
 * plates, then cut to five labelled category plates on 2026-09-14 (morning). The reason for the
 * vertical dock died with the 22 plates; what the dock still cost was measured the same day
 * (`docs/demos/slot-rail-demo.html`): one-word labels ("Back" reads as *go back*), a count badge
 * pinned over the very glyph it counts, a fifth plate that fell below the rail on any viewport
 * under ~760 tall, and 60 of the band's 412 spent on a column the pet's fit was carved around.
 * A row of five shows all five at every height, with every label spelled out, and the badge sits
 * beside the glyph instead of on it. The pet is centred in the full band.
 *
 * It lives in the DRAWER, not the preview band, on purpose: it filters the grid directly beneath
 * it, and it is a row of chrome in a panel made of chrome. In the band it would sit over the
 * artwork's lower margin — where the pet's feet are.
 *
 * A group renders locked only when EVERY slot in it is below the companion's stage — with the
 * shipped pack no group is ever fully locked, but the rule stops a future pack producing a tab
 * that opens an empty drawer. A slot the server does not list at all is treated as absent, not
 * locked: membership comes from SLOT_GROUPS so the strip is always five wide.
 *
 * A `projected` entry (transmog projection, see EquippedSlot's own doc comment in
 * api/wardrobe.ts) is not a wardrobe piece: it does not count toward the badge and does
 * not appear in the name. It still renders in the preview and is still absent from the grid (it
 * is not owned), so nothing here invites a tap that could remove it.
 */
export function SlotRail({
  slotDefs, equipped, stage, activeGroup, onSelectGroup, itemNamesByCode, orientation = 'strip',
}: SlotRailProps) {
  const rank = stageRank(stage);
  const defsByCode = new Map(slotDefs.map((d) => [d.code, d] as const));
  const list = orientation === 'list';
  return (
    <div className={list ? 'slot-rail slot-rail-list' : 'slot-rail'} role="region" aria-label="Wardrobe categories">
      {SLOT_GROUPS.map((group) => {
        const served = group.slots.map((s) => defsByCode.get(s)).filter((d): d is SlotDto => d != null);
        const fullyLocked = served.length > 0 && served.every((d) => rank < stageRank(d.minStage));
        const worn = group.slots
          .map((s) => equipped[s])
          .filter((e): e is EquippedSlot => e != null && !e.projected);
        const wornNames = worn.map((e) => itemNamesByCode?.[e.itemCode] ?? e.itemCode);
        const isActive = group.code === activeGroup;

        if (fullyLocked) {
          return (
            <div key={group.code} className="slot-rail-item slot-locked" aria-disabled="true">
              <span className="slot-glyph slot-glyph-locked" aria-hidden="true"><GroupGlyph code={group.code} /></span>
              <span className="slot-label">{group.label}</span>
              <span className="slot-hint">Level up to unlock</span>
            </div>
          );
        }
        return (
          <button
            key={group.code}
            type="button"
            className={`slot-rail-item${isActive ? ' slot-active' : ''}`}
            aria-pressed={isActive}
            aria-label={wornNames.length ? `${group.label} — ${wornNames.join(', ')}` : group.label}
            onClick={() => onSelectGroup(group.code)}
          >
            {/* The badge is a sibling of the glyph, not a child pinned to its corner — on the dock
                it covered the glyph's top-right quadrant on four of five plates once the pet was
                dressed. Here it sits in the tab's own corner, clear of both glyph and label. */}
            {worn.length > 0 && <span className="slot-count" aria-hidden="true">{worn.length}</span>}
            <span className="slot-glyph" aria-hidden="true"><GroupGlyph code={group.code} /></span>
            {/* aria-hidden: the aria-label above already carries the full label — without this the
                accessible name would read "Head Head". */}
            <span className="slot-label" aria-hidden="true">{group.label}</span>
            {/* List rows only: what is worn here, as the pieces' own icons. Decorative — the
                accessible name above already lists them by name, so `alt=""`. The strip has no
                room for these; the count badge does that job there. */}
            {list && worn.length > 0 && (
              <span className="slot-thumbs" aria-hidden="true">
                {worn.map((e) => (
                  <img key={e.itemCode} className="slot-thumb" src={itemIconPath(e.itemCode)} alt="" />
                ))}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
